import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'

import { getFloorItemsAbove } from '../../prepItemFloors'
import { IScopeOption } from '../gql'

export interface IEditableRule {
  id: string
  locationGroupId: number | null
  value: string
}

interface IRuleRowsProps {
  rules: IEditableRule[]
  scopes: IScopeOption[]
  disabled?: boolean
  onChange: (rules: IEditableRule[]) => void
}

const ALL_LOCATIONS = 'all'

const generateId = () => Math.random().toString(36).substring(2, 9)

const floorWarning = (value: string): string | null => {
  const numericValue = parseFloat(value)
  if (value.trim() === '' || isNaN(numericValue)) return null

  const held = getFloorItemsAbove(numericValue)
  if (held.length === 0) return null

  const names = held
    .map((item) => `${item.label} (${item.floorPercent}%)`)
    .join(', ')
  return `Below the guaranteed minimum for ${names}. Those items stay at their minimum; everything else follows ${numericValue}%.`
}

const RuleRows = ({ rules, scopes, disabled, onChange }: IRuleRowsProps) => {
  const usedKeys = new Set(
    rules.map((rule) =>
      rule.locationGroupId === null
        ? ALL_LOCATIONS
        : String(rule.locationGroupId),
    ),
  )
  // why: only a group knows its own reach; the fleet total is not something
  // list_prep_setting_scopes returns, and summing the states would guess at it.
  const storeCountFor = (locationGroupId: number | null) =>
    locationGroupId === null
      ? null
      : scopes.find((scope) => scope.locationGroupId === locationGroupId)
          ?.storeCount ?? 0

  const firstUnusedScope = (): number | null => {
    if (!usedKeys.has(ALL_LOCATIONS)) return null
    const free = scopes.find(
      (scope) => !usedKeys.has(String(scope.locationGroupId)),
    )
    return free ? free.locationGroupId : null
  }

  const canAdd = usedKeys.size < scopes.length + 1

  const handleAdd = () => {
    onChange([
      ...rules,
      { id: generateId(), locationGroupId: firstUnusedScope(), value: '' },
    ])
  }

  const handleDelete = (id: string) => {
    onChange(rules.filter((rule) => rule.id !== id))
  }

  const handleScopeChange = (id: string, selected: string) => {
    const locationGroupId =
      selected === ALL_LOCATIONS ? null : parseInt(selected, 10)
    onChange(
      rules.map((rule) =>
        rule.id === id ? { ...rule, locationGroupId } : rule,
      ),
    )
  }

  const handleValueChange = (id: string, value: string) => {
    onChange(rules.map((rule) => (rule.id === id ? { ...rule, value } : rule)))
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          px: 0.5,
          pb: 0.5,
          fontSize: 11.5,
          letterSpacing: '0.03em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          fontWeight: 600,
        }}
      >
        <Box sx={{ flex: 1 }}>Applies to</Box>
        <Box sx={{ width: 110 }}>Increase %</Box>
        <Box sx={{ width: 34 }} />
      </Box>

      {rules.map((rule) => {
        const warning = floorWarning(rule.value)
        const selected =
          rule.locationGroupId === null
            ? ALL_LOCATIONS
            : String(rule.locationGroupId)

        return (
          <Box key={rule.id} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Select
                value={selected}
                size="small"
                disabled={disabled}
                onChange={(e) => handleScopeChange(rule.id, e.target.value)}
                sx={{ flex: 1 }}
              >
                <MenuItem
                  value={ALL_LOCATIONS}
                  disabled={
                    selected !== ALL_LOCATIONS && usedKeys.has(ALL_LOCATIONS)
                  }
                >
                  All locations
                </MenuItem>
                {scopes.map((scope) => (
                  <MenuItem
                    key={scope.locationGroupId}
                    value={String(scope.locationGroupId)}
                    disabled={
                      selected !== String(scope.locationGroupId) &&
                      usedKeys.has(String(scope.locationGroupId))
                    }
                  >
                    {`${scope.scopeType} / region: ${scope.scopeName}`}
                  </MenuItem>
                ))}
              </Select>
              <Typography
                sx={{ color: 'text.secondary', fontSize: 12.5, width: 68 }}
              >
                {storeCountFor(rule.locationGroupId) === null
                  ? ''
                  : `${storeCountFor(rule.locationGroupId)} stores`}
              </Typography>
              <TextField
                value={rule.value}
                onChange={(e) => {
                  const inputValue = e.target.value
                  if (inputValue === '' || /^-?\d*\.?\d*$/.test(inputValue)) {
                    handleValueChange(rule.id, inputValue)
                  }
                }}
                size="small"
                placeholder="%"
                disabled={disabled}
                sx={{ width: 110 }}
              />
              <IconButton
                size="small"
                color="error"
                disabled={disabled}
                onClick={() => handleDelete(rule.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {warning && (
              <Alert severity="warning" sx={{ mt: 0.5, fontSize: 13 }}>
                {warning}
              </Alert>
            )}
          </Box>
        )
      })}

      {rules.length === 0 && (
        <Typography sx={{ py: 1.5, fontSize: 13.5, color: 'text.secondary' }}>
          No rules. Every location keeps its prep amount unchanged.
        </Typography>
      )}

      {canAdd && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          disabled={disabled}
          onClick={handleAdd}
          sx={{ textTransform: 'none' }}
        >
          Add rule
        </Button>
      )}
    </Box>
  )
}

export default RuleRows
